import {ReactElement, useContext, useEffect, useMemo, useState} from "react";
import {ViziblyTheme} from "../../../../styles/zendesk-garden/ViziblyZDGTheme";
import WorkflowHeader from "../../../../components/workflows/workflow-header/WorkflowHeader";
import WorkflowNavToggle from "../../../../components/workflows/workflow-nav-toggle/WorkflowNavToggle";
import WorkflowPageLabel from "../../../../components/workflows/workflow-page-label/WorkflowPageLabel";
import VZSaveStatus from "../../../../components/vz-ui/vz-save-status/VZSaveStatus";
import WorkflowStepNav from "../../../../components/workflows/workflow-step-nav/WorkflowStepNav";
import {ThemeProvider} from "styled-components";
import * as workflowHeaderCSS from "../../../../components/workflows/workflow-header/styles/css.module.scss";
import * as workflowCSS from "../../../../styles/workflows/workflowCSS.module.scss";
import {IWorkflowPageProps, shouldUpdateWorkflowTab} from "../../logic/workflows";
import {useProperties} from "../../../../contexts/properties/PropertiesContext";
import {Tabs} from "@zendeskgarden/react-tabs";
import WorkflowYearTabs, {
    WORKFLOW_YEAR_TAB_BUDGET
} from "../../../../components/workflows/workflow-year-tabs/WorkflowYearTabs";
import {AuthContext} from "../../../../contexts/AuthContext";
import {
    OriginalRevenueUnitTypeNewLeaseLossLeaderInput, useGetForecastLocksLazyQuery,
    useListOriginalRevenueUnitTypeNewLeaseLossLeadersLazyQuery,
    useUpsertOriginalRevenueUnitTypeNewLeaseLossLeaderMutation,
    VersionType
} from "../../../../__generated__/generated_types";
import {getMonths} from "../../../../utils/months-helper";
import OperationalVZHotTable from "../OperationalVZHotTable";
import {IVZHotTableRow} from "../../../../components/vz-ui/vz-hot-table/VZHotTable";
import {VZHotTableConfig} from "../../../../components/vz-ui/vz-hot-table/vz-hot-table-config";
import {buildUTSVTableConfig} from "../utils/operational-metric-helpers";
import {useParams} from "react-router-dom";
import {BudgetingType} from "../../../../BudgetingType";
import {toast} from "react-toastify";

const NUM_ROW_HEADERS = 2;

const MOVE_IN_MONTH_FIELD: Map<number, keyof OriginalRevenueUnitTypeNewLeaseLossLeaderInput> = new Map([
    [0, "moveInPercentMonth0"],
    [1, "moveInPercentMonth1"],
    [2, "moveInPercentMonth2"],
    [3, "moveInPercentMonth3"],
    [4, "moveInPercentMonth4"],
    [5, "moveInPercentMonth5"],
    [6, "moveInPercentMonth6"],
    [7, "moveInPercentMonth7"],
    [8, "moveInPercentMonth8"],
    [9, "moveInPercentMonth9"],
    [10, "moveInPercentMonth10"],
    [11, "moveInPercentMonth11"]
]);

const DISCOUNT_MONTH_FIELD: Map<number, keyof OriginalRevenueUnitTypeNewLeaseLossLeaderInput> = new Map([
    [0, "discountPercentMonth0"],
    [1, "discountPercentMonth1"],
    [2, "discountPercentMonth2"],
    [3, "discountPercentMonth3"],
    [4, "discountPercentMonth4"],
    [5, "discountPercentMonth5"],
    [6, "discountPercentMonth6"],
    [7, "discountPercentMonth7"],
    [8, "discountPercentMonth8"],
    [9, "discountPercentMonth9"],
    [10, "discountPercentMonth10"],
    [11, "discountPercentMonth11"]
]);

export function LossLeaders(props: IWorkflowPageProps): ReactElement {
    const {type} = useParams() as {type: BudgetingType;}
    const {currentProperty} = useProperties();
    const {budgetYear} = useContext(AuthContext);

    const [selectedTab, setSelectedTab] = useState(WORKFLOW_YEAR_TAB_BUDGET);

    const [lastSaved, setLastSaved] = useState<Date>(new Date());

    const [getForecastLocks, { data: forecastLocksData }] = useGetForecastLocksLazyQuery({fetchPolicy: "no-cache"});

    const [getLossLeaders, {data, loading}] = useListOriginalRevenueUnitTypeNewLeaseLossLeadersLazyQuery({
        fetchPolicy: "no-cache"
    });

    const [upsertLossLeaders, {loading: upsertLossLeadersLoading}] = useUpsertOriginalRevenueUnitTypeNewLeaseLossLeaderMutation({
        fetchPolicy: "no-cache"
    });

    const [toSave, setToSave] = useState<OriginalRevenueUnitTypeNewLeaseLossLeaderInput[]>([]);

    const versionType = selectedTab === WORKFLOW_YEAR_TAB_BUDGET ? VersionType.Budget : VersionType.Reforecast;

    useEffect(() => {
        if(upsertLossLeadersLoading || toSave.length === 0) {
            return;
        }

        const localCopy = toSave;
        setToSave([]);

        upsertLossLeaders({
            variables: {
                args: localCopy
            }
        }).then(() => {
            setLastSaved(new Date());
        }).catch(() => {
            toast.error("Failed to save values.");
        });
    }, [toSave, upsertLossLeadersLoading]);

    useEffect(() => {
        const updateWorkflowTab = shouldUpdateWorkflowTab(type, selectedTab);
        if (updateWorkflowTab != false) {
            setSelectedTab(updateWorkflowTab as string);
        }
    }, [type]);

    useEffect(() => {
        if(!currentProperty) {
            return;
        }

        getLossLeaders({
            variables: {
                propertyId: currentProperty.id,
                budgetYear: budgetYear,
                versionType: versionType
            }
        });

        getForecastLocks({
            variables: {
                budgetYear: budgetYear,
                propertyId: currentProperty.id
            }
        });
    }, [currentProperty, budgetYear, versionType]);

    const {tableData, tableConfig} = useMemo<{tableData: IVZHotTableRow[], tableConfig: VZHotTableConfig}>(() => {
        if (!data || !currentProperty) {
            return {
                tableData: [],
                tableConfig: new VZHotTableConfig()
            };
        }

        function getVal(val: string | null | undefined): number {
            if (val === null || val === undefined) {
                return 0;
            }
            const parsed = parseFloat(val);
            if(isNaN(parsed)) {
                return 0;
            }
            return parsed;
        }

        const moveInSummaries: number[] = new Array(12).fill(0);
        const discountSummaries: number[] = new Array(12).fill(0);

        const tableData: IVZHotTableRow[] = [];
        const lossLeadersSorted = data.listOriginalRevenueUnitTypeNewLeaseLossLeaders.sortBy(v => v.unitType?.name ?? "");
        for(const lossLeader of lossLeadersSorted) {
            tableData.push({
                rowId: `${lossLeader.unitTypeId}-moveins`,
                parentRowId: "moveInSummary",
                rowType: "generic",
                label: lossLeader.unitType?.name ?? "",
                values: lossLeader.moveInPercent,
                extraData: {
                    label1: "Move Ins",
                    unitTypeId: lossLeader.unitTypeId,
                    dataType: "moveIn"
                }
            });

            tableData.push({
                rowId: `${lossLeader.unitTypeId}-discount`,
                parentRowId: "discountSummary",
                rowType: "generic",
                label: lossLeader.unitType?.name ?? "",
                values: lossLeader.discountPercent,
                extraData: {
                    label1: "Discount",
                    unitTypeId: lossLeader.unitTypeId,
                    dataType: "discount"
                }
            });

            for(let i = 0; i < 12; i++) {
                moveInSummaries[i] += getVal(lossLeader.moveInPercent[i]);
                discountSummaries[i] += getVal(lossLeader.discountPercent[i]);
            }
        }

        for(let i = 0; i < 12; i++) {
            moveInSummaries[i] = moveInSummaries[i]! / lossLeadersSorted.length;
            discountSummaries[i] = discountSummaries[i]! / lossLeadersSorted.length;
        }

        tableData.unshift({
            rowId: "discountSummary",
            rowType: "summary",
            label: "",
            parentRowSummaryFunction: "avg",
            values: discountSummaries,
            extraData: {
                label1: "Discount"
            }
        });

        tableData.unshift({
            rowId: "moveInSummary",
            rowType: "summary",
            label: "",
            parentRowSummaryFunction: "avg",
            values: moveInSummaries,
            extraData: {
                label1: "Move Ins"
            }
        });

        const tableConfig = buildUTSVTableConfig(
                tableData,
                (versionType == VersionType.Budget ? forecastLocksData?.forecastLocks?.property.budgetLocked : forecastLocksData?.forecastLocks?.property.reforecastLocked) ?? false,
                versionType,
                currentProperty.reforecastStartMonthIndex,
                "percent",
                "avg",
                NUM_ROW_HEADERS,
                2
        );

        return {tableData, tableConfig};
    }, [data, versionType, currentProperty, forecastLocksData]);

    function handleTableChanges(changes: [number, number, number][]): void {
        if(changes.length == 0) {
            return;
        }

        const adjustedChanges: Record<string, OriginalRevenueUnitTypeNewLeaseLossLeaderInput> = {};
        for(const change of changes) {
            const [row, month, newVal] = change;
            const hotRow = tableData[row];
            const unitTypeId = hotRow?.extraData?.["unitTypeId"];
            const dataType = hotRow?.extraData?.["dataType"];
            if(typeof unitTypeId !== "string" || (dataType !== "moveIn" && dataType !== "discount")) {
                continue;
            }

            let existing = adjustedChanges[unitTypeId];
            if(!existing) {
                existing = {
                    unitTypeId: unitTypeId,
                    budgetYear: budgetYear,
                    versionType: versionType
                };
                adjustedChanges[unitTypeId] = existing;
            }

            // Ignore the type check on this. The return type of the change
            // function is wrong and it isn't always a number.
            let adjustedVal: string;
            if(typeof newVal === "number") {
                adjustedVal = newVal.toString();
            } else if(typeof newVal === "string") {
                adjustedVal = newVal;
            } else {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                adjustedVal = newVal.toString();
            }
            if(dataType === "moveIn") {
                const field = MOVE_IN_MONTH_FIELD.get(month);
                if(field) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    existing[field] = adjustedVal;
                }
            } else if(dataType === "discount") {
                const field = DISCOUNT_MONTH_FIELD.get(month);
                if(field) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    existing[field] = adjustedVal;
                }
            }
        }

        setToSave(Object.values(adjustedChanges));
    }

    return <ThemeProvider theme={ViziblyTheme}>
        <div className={`${workflowCSS.workflowContainer} ${workflowCSS.revenueScreen}`}>
            <WorkflowHeader>
                <WorkflowHeader.LeftCol>
                    <div className={workflowHeaderCSS.rowItemsContainer}>
                        <WorkflowNavToggle inUserWorkflow={props.inUserWorkflow} />
                        <WorkflowPageLabel label="Move-In Discount" />
                    </div>
                </WorkflowHeader.LeftCol>
                <WorkflowHeader.RightCol>
                    <VZSaveStatus isSaving={toSave.length > 0 || upsertLossLeadersLoading} lastSaved={lastSaved} />
                    <WorkflowStepNav onPreviousClick={props.onPreviousClick} onNextClick={props.onNextClick} inUserWorkflow={props.inUserWorkflow} />
                </WorkflowHeader.RightCol>
            </WorkflowHeader>
            <div className={workflowCSS.bodyContainer}>
                {currentProperty &&
                    <>
                        <Tabs selectedItem={selectedTab} onChange={setSelectedTab} className={workflowCSS.tabsContainer}>
                            <WorkflowYearTabs
                                    property={currentProperty}
                                    selectedTab={selectedTab}
                                    rawForecastLocks={forecastLocksData}
                            />
                        </Tabs>

                        <div className={workflowCSS.tableWrapper}>
                            <OperationalVZHotTable
                                data={tableData}
                                numRowHeaderCols={NUM_ROW_HEADERS}
                                colWidths={[80]}
                                colHeaders={["Unit Type", "Metric", ...getMonths(currentProperty), "Average"]}
                                fixedRowsTop={2}
                                tableConfig={tableConfig}
                                isDataLoading={loading}
                                isSearchable
                                isUndoRedoable
                                summaryColumns={[
                                    {
                                        col: 14,
                                        summaryFunction: "avg",
                                    },
                                ]}
                                onChange={changes => handleTableChanges(changes)}
                            />
                        </div>
                    </>
                }
            </div>
        </div>
    </ThemeProvider>;
}